import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Switch,
  TabsProps,
} from 'antd';

import ReferencesSelector, {
  EMPTY_ID,
} from '@totem/components/common/reference/ReferenceSelector';
import TicketConfigurationContext from '@totem/components/ticketing/configuration/ticketingConfigurationContext';
import PartyInvolvedEntry from '@totem/components/ticketing/ticket/partyInvolvedEntry';
import OptimizeForm from '@totem/components/ticketing/ticket/specialCases/optimizeForm';
import ProjectTicketCreate from '@totem/components/ticketing/ticket/specialCases/projectTicketCreate';
import RemoteAccessOverlayForm from '@totem/components/ticketing/ticket/specialCases/remoteAccessOverlayForm';
import {
  MIN_DATE_TIME_STRING,
  RelatedTicketReference,
  Ticket,
  TicketCategory,
  TicketQueryResult,
} from '@totem/components/ticketing/ticketing';
import {
  defaultTicket,
  getDefaultTicketTypes,
} from '@totem/components/ticketing/utilities';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import { buildingDisplayName } from '@totem/components/ui_components/constants';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { emptyId } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { ForeignKey, Reference } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import { controlSystemTypeOptions } from '@totem/utilities/controlSystemsUtilities';
import { TICKET_ENDPOINT } from '@totem/utilities/endpoints';
const FormItem = Form.Item;
const { Option } = Select;
const { TextArea } = Input;
const styles = {
  form: {
    width: '100%',
  },
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
  },
  summaryInput: {
    width: '425px',
  },
};
const defaultTicketType: string = 'Service Request';

export interface Props {
  regionId?: string;
  buildingId?: string;
  controlSystemId?: string;
  deviceId?: string;
  keys?: ForeignKey[];
  visible: boolean;
  onClose: (ticketCreated: boolean) => void;
  defaultSummary?: string;
  defaultDescription?: string;
  defaultCategory?: string;
  defaultSubCategory?: string;
  relatedTickets?: RelatedTicketReference[];
  partyInvolvedTypes?: string[];
  navigateToNewTicket?: boolean;
}

const TicketEntryDialog = ({
  visible,
  onClose,
  regionId,
  buildingId,
  controlSystemId,
  deviceId,
  defaultSummary,
  defaultDescription,
  defaultCategory,
  defaultSubCategory,
  keys,
  relatedTickets,
  partyInvolvedTypes,
  navigateToNewTicket = true,
}: Props) => {
  const partyInvolvedTypeList = isNotNull(partyInvolvedTypes)
    ? partyInvolvedTypes
    : [];
  const navigate = useNavigate();
  const { filteredCategories } = useContext(TicketConfigurationContext);
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [typeCategories, setTypeCategories] =
    useState<TicketCategory[]>(filteredCategories);
  const [isSending, setIsSending] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<TicketCategory[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<
    TicketCategory[]
  >([]);
  const [ticketType, setTicketType] = useState<string>(defaultTicketType);
  const [ticketData, setTicketData] = useState<Ticket>({
    ...defaultTicket,
    buildingId: buildingId ? buildingId : emptyId,
    controlSystemId: controlSystemId ? controlSystemId : emptyId,
    description:
      typeof defaultDescription !== 'undefined' && defaultDescription !== null
        ? defaultDescription
        : '',
    deviceId: deviceId ? deviceId : emptyId,
    keys: isNotNull(keys) ? keys : [],
    partiesInvolved: [
      {
        id: '',
        type: 'Requester',
        status: 'ACTIVE',
        notify: true,
        name: `${userProfile.lastName}, ${userProfile.firstName}`,
        email: userProfile.email,
        phone: userProfile.phoneNumber,
        involvementStart: MIN_DATE_TIME_STRING,
        involvementEnd: MIN_DATE_TIME_STRING,
        createdByType: 'USER',
        createdByName: userProfile.email,
        createdById: userProfile.id,
        keys: [],
      },
      ...partyInvolvedTypeList.map((typeName) => {
        return {
          id: '',
          type: typeName,
          status: 'ACTIVE',
          notify: true,
          name: '',
          email: '',
          phone: '',
          involvementStart: MIN_DATE_TIME_STRING,
          involvementEnd: MIN_DATE_TIME_STRING,
          createdByType: 'USER',
          createdByName: userProfile.email,
          createdById: userProfile.id,
          keys: [],
        };
      }),
    ],
    regionId: regionId ? regionId : emptyId,
    status: 'OPEN',
    summary:
      typeof defaultSummary !== 'undefined' && defaultSummary !== null
        ? defaultSummary
        : '',
    type: defaultTicketType,
    category:
      typeof defaultCategory !== 'undefined' && defaultCategory !== null
        ? defaultCategory
        : '',
    subCategory:
      typeof defaultSubCategory !== 'undefined' && defaultSubCategory !== null
        ? defaultSubCategory
        : '',
    organizationId: userProfile.organization.id,
    relatedTickets: isNotNull(relatedTickets) ? relatedTickets : [],
  });

  // Ticket Types & Categories
  useEffect(() => {
    setTypeCategories(filteredCategories);
  }, [filteredCategories]);

  // Ticket Type Specific Data
  const [projectControlSystemType, setProjectControlSystemType] =
    useState<string>('');

  // Update Categories when Ticket Type changes
  useEffect(() => {
    if (isNotNull(ticketType) && ticketType !== '' && typeCategories !== null) {
      const selectedTicketType = typeCategories.find(
        (chk) => chk.name === ticketType,
      );
      if (
        isNotNull(selectedTicketType) &&
        isNotNull(selectedTicketType.children)
      ) {
        setCategoryOptions(selectedTicketType.children);
        if (selectedTicketType.children.length > 0) {
          if (isNotNull(ticketData)) {
            setTicketData((prevState) => ({
              ...prevState,
              category: selectedTicketType.children[0].name,
            }));
          }
          return;
        }
      }
    }
    setCategoryOptions([]);
  }, [ticketData.type, filteredCategories]);

  // Update SubCategories when Category changes
  useEffect(() => {
    if (isNotNull(ticketData.category)) {
      const newCategory = categoryOptions.find(
        (chk) => chk.name === ticketData.category,
      );
      if (isNotNull(newCategory)) {
        setSubCategoryOptions(newCategory.children);
        if (newCategory.children.length > 0) {
          if (isNotNull(ticketData)) {
            setTicketData((prevState) => ({
              ...prevState,
              subCategory: newCategory.children[0].name,
            }));
          }
          return;
        }
      }
    }
    //setCategoryOptions([]);
  }, [ticketData.category, filteredCategories]);

  const sendTicket = useCallback(
    async (request: Ticket) => {
      if (isSending) {
        return;
      }
      fetch(`${TICKET_ENDPOINT}`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(request),
      })
        .then((response) => {
          if (response.status === 200) {
            onClose(true);
            return response.json();
          }
          message.error('Ticket creation failed!');
          return response.json();
        })
        .then((result: TicketQueryResult) => {
          if (navigateToNewTicket) {
            const ticketId = result.ticket.id;
            navigate(`/dashboard/ticket/${ticketId}`);
          }
        })
        .finally(() => {
          setIsSending(false);
        });

      setIsSending(true);
    },
    [isSending],
  );

  const isValid = () => {
    return true;
  };

  const handleSubmit = () => {
    setFormSubmitted(true);
    if (isValid()) {
      if (isNotNull(ticketData)) {
        const payload = { ...ticketData };
        if (
          payload.type === 'Site Project' &&
          payload.controlSystemId === EMPTY_ID &&
          projectControlSystemType !== ''
        ) {
          // ONLY FOR "Site Project" TICKETS
          payload.attributes.push({
            source: 'SiteProject',
            name: 'ControlSystemType',
            dataType: 'string',
            value: projectControlSystemType,
          });
        }
        if (payload.type === 'Project' || payload.type === 'HR') {
          for (let idx = 0; idx < payload.partiesInvolved.length; idx++) {
            if (payload.partiesInvolved[idx].type === 'Requester') {
              payload.partiesInvolved[idx].type = 'Project Manager';
            }
          }
        }
        payload.partiesInvolved = payload.partiesInvolved.filter(
          (chk) => chk.name !== '' || chk.email !== '',
        );
        if (
          !permissions.internal.hasPermissions(userProfile) &&
          payload.internalOnly
        ) {
          payload.internalOnly = false;
        }
        sendTicket(payload);
      }
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    if (name === 'type') {
      setTicketType(value);
      if (
        (value === 'Project' || value === 'HR') &&
        ticketData.internalOnly === false
      ) {
        setTicketData((prevState) => ({ ...prevState, internalOnly: true }));
      } else if (ticketData.internalOnly === true) {
        setTicketData((prevState) => ({ ...prevState, internalOnly: false }));
      }
    }
    if (name === 'category') {
      setTicketData((prevState) => ({
        ...prevState,
        [name]: value,
        subCategory: '',
      }));
    } else {
      setTicketData((prevState) => ({ ...prevState, [name]: value }));
    }
  };

  const handleChange = ({
    target: { value, name },
  }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTicketData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleReferenceChange = (ref: Reference) => {
    setTicketData((prevState) => ({
      ...prevState,
      regionId: ref.regionId,
      buildingId: ref.buildingId,
      controlSystemId: ref.controlSystemId,
    }));
  };

  const getTabItems = (): TabsProps['items'] => {
    const tabItems: TabsProps['items'] = [
      {
        key: 'Description',
        label: 'Description',
        children: (
          <>
            <FormItem
              label="Summary"
              colon={false}
              style={styles.formItem}
              required
              validateStatus={
                formSubmitted === false || ticketData.summary.trim() !== ''
                  ? 'success'
                  : 'error'
              }
            >
              <Input
                name="summary"
                type="text"
                style={styles.summaryInput}
                value={ticketData.summary}
                onChange={handleChange}
                maxLength={50}
              />
            </FormItem>
            <FormItem label="Description" colon={false} style={styles.formItem}>
              <TextArea
                name="description"
                value={ticketData.description}
                onChange={handleChange}
              />
            </FormItem>
          </>
        ),
      },
    ];
    if (ticketType === 'Project' && isNotNull(ticketData)) {
      tabItems.push({
        key: 'Project',
        label: 'Project',
        children: (
          <ProjectTicketCreate
            isEdit={false}
            project={ticketData.project}
            onChange={(projUpd) => {
              setTicketData((prevState) => ({
                ...prevState,
                project: projUpd,
              }));
            }}
          />
        ),
      });
    }
    if (
      isNotNull(ticketData) &&
      ticketData.category === 'Optimize' &&
      ticketData.subCategory === 'Opportunity'
    ) {
      tabItems.push({
        key: 'Optimize',
        label: 'Optimize',
        children: (
          <OptimizeForm
            optimize={ticketData.extras.optimize}
            onChange={(optimize) => {
              setTicketData((prevState) => ({
                ...prevState,
                extras: { ...prevState.extras, optimize },
              }));
            }}
          />
        ),
      });
    }
    if (
      isNotNull(ticketData) &&
      ticketData.category === 'Remote Access' &&
      ticketData.subCategory === 'Enable Access'
    ) {
      tabItems.push({
        key: 'Remote Access Group/Overlay',
        label: 'Remote Access Group/Overlay',
        children: (
          <RemoteAccessOverlayForm
            overlayNetwork={ticketData.extras.overlayNetwork}
            onChange={(overlayNetwork) => {
              setTicketData((prevState) => ({
                ...prevState,
                extras: { ...prevState.extras, overlayNetwork },
              }));
            }}
          />
        ),
      });
    }
    if (
      isNotNull(ticketData) &&
      ticketType === 'Site Project' &&
      ticketData.controlSystemId === EMPTY_ID
    ) {
      tabItems.push({
        key: 'ControlSystem',
        label: 'Control System',
        children: (
          <FormItem
            label="Control System Type"
            colon={false}
            style={styles.formItem}
            validateStatus={
              formSubmitted === false || ticketData.summary.trim() !== ''
                ? 'success'
                : 'error'
            }
          >
            <Select
              value={projectControlSystemType}
              onChange={(newValue: string) =>
                setProjectControlSystemType(newValue)
              }
            >
              {controlSystemTypeOptions.map((option) => {
                return (
                  <Option key={option.value} value={option.text}>
                    {option.text}
                  </Option>
                );
              })}
            </Select>
          </FormItem>
        ),
      });
    }
    if (permissions.internal.hasPermissions(userProfile, null)) {
      tabItems.push({
        key: 'Requester',
        label: 'Requester',
        children: (
          <PartyInvolvedEntry
            partyInvolvedIndex={0}
            partyInvolvedType={'Requester'}
            ticketData={ticketData}
            setTicketData={setTicketData}
          />
        ),
      });
    }
    for (let idx = 0; idx < partyInvolvedTypeList.length; idx++) {
      tabItems.push({
        key: partyInvolvedTypeList[idx],
        label: partyInvolvedTypeList[idx],
        children: (
          <PartyInvolvedEntry
            key={idx}
            partyInvolvedIndex={idx + 1}
            partyInvolvedType={partyInvolvedTypeList[idx]}
            ticketData={ticketData}
            setTicketData={setTicketData}
          />
        ),
      });
    }
    return tabItems;
  };

  return (
    <Modal
      open={visible}
      onCancel={() => onClose(false)}
      title={<ModalTitle>Create Ticket</ModalTitle>}
      confirmLoading={false}
      width={'40%'}
      footer={
        <ModalFooter>
          <Button onClick={() => onClose(false)}>Close</Button>
          <Button onClick={handleSubmit} type="primary" disabled={isSending}>
            Submit
          </Button>
        </ModalFooter>
      }
    >
      <Form layout="vertical" style={styles.form}>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <FormItem
              label="Ticket Type"
              colon={false}
              style={styles.formItem}
              required
              tooltip={{
                title: (
                  <>
                    <b>Incident:</b> Unplanned interruption or quality reduction
                    of a service.
                    <br />
                    <b>Problem:</b> Underlying cause of one or more incidents.
                    <br />
                    <b>Service Request:</b> Request for information, advice, or
                    a standard service.
                    <br />
                    <b>Change Request:</b> Formal request to modify a service,
                    system, or process.
                    <br />
                    <b>Project:</b> Time-bound initiative with defined
                    deliverables.
                    <br />
                    <b>Site Project:</b> Client managed project scoped to a
                    {` ${buildingDisplayName}`}.
                  </>
                ),
              }}
            >
              <Select
                value={ticketData.type}
                onChange={(newValue: string) => {
                  setTicketType(newValue);

                  handleSelectChange('type', newValue);
                }}
              >
                {isNotNull(typeCategories) && typeCategories.length > 0
                  ? typeCategories.map((type) => (
                      <Option key={type.name} value={type.name}>
                        {type.name}
                      </Option>
                    ))
                  : getDefaultTicketTypes().map((type) => (
                      <Option key={type} value={type}>
                        {type}
                      </Option>
                    ))}
              </Select>
            </FormItem>
            <FormItem
              label="Impact"
              colon={false}
              style={styles.formItem}
              required
              tooltip={{
                title: (
                  <>
                    How broadly this issue affects the business:
                    <br />
                    <b>High:</b> Organization-wide or critical function down.
                    <br />
                    <b>Medium:</b> Department or significant group affected.
                    <br />
                    <b>Low:</b> Single user or non-critical function.
                  </>
                ),
              }}
            >
              <Select
                value={ticketData.impact}
                onChange={(newValue: string) =>
                  handleSelectChange('impact', newValue)
                }
              >
                <Option value="High">High</Option>
                <Option value="Medium">Medium</Option>
                <Option value="Low">Low</Option>
              </Select>
            </FormItem>
            <FormItem
              label="Urgency"
              colon={false}
              style={styles.formItem}
              required
              tooltip={{
                title: (
                  <>
                    How quickly this issue needs to be resolved:
                    <br />
                    <b>High:</b> Immediate action required, no workaround
                    exists.
                    <br />
                    <b>Medium:</b> Timely but not immediate, partial workaround
                    available.
                    <br />
                    <b>Low:</b> No time pressure, workaround exists.
                  </>
                ),
              }}
            >
              <Select
                value={ticketData.urgency}
                onChange={(newValue: string) =>
                  handleSelectChange('urgency', newValue)
                }
              >
                <Option value="High">High</Option>
                <Option value="Medium">Medium</Option>
                <Option value="Low">Low</Option>
              </Select>
            </FormItem>
            {permissions.internal.hasPermissions(userProfile) && (
              <FormItem
                label="Internal Only"
                colon={false}
                style={styles.formItem}
              >
                <Switch
                  checked={ticketData.internalOnly}
                  onChange={(isChecked: boolean) =>
                    setTicketData((prevState) => ({
                      ...prevState,
                      internalOnly: isChecked,
                    }))
                  }
                />
              </FormItem>
            )}
          </Col>
          <Col span={8}>
            <FormItem
              label="Category"
              colon={false}
              style={styles.formItem}
              required
              validateStatus={
                formSubmitted === false || ticketData.category !== ''
                  ? 'success'
                  : 'error'
              }
            >
              <Select
                value={ticketData.category}
                onChange={(newValue: string) => {
                  handleSelectChange('category', newValue);
                }}
              >
                <Option value="">--Select--</Option>
                {isNotNull(categoryOptions) &&
                  categoryOptions.map((category) => (
                    <Option key={category.name} value={category.name}>
                      {category.name}
                    </Option>
                  ))}
              </Select>
            </FormItem>
            <FormItem
              label="SubCategory"
              colon={false}
              style={styles.formItem}
              required
              validateStatus={
                formSubmitted === false || ticketData.subCategory !== ''
                  ? 'success'
                  : 'error'
              }
            >
              <Select
                value={ticketData.subCategory}
                onChange={(newValue: string) =>
                  handleSelectChange('subCategory', newValue)
                }
              >
                <Option value="">--Select--</Option>
                {isNotNull(subCategoryOptions) &&
                  subCategoryOptions.map((subCategory) => (
                    <Option key={subCategory.name} value={subCategory.name}>
                      {subCategory.name}
                    </Option>
                  ))}
              </Select>
            </FormItem>
          </Col>
          <Col span={8}>
            <ReferencesSelector
              onChanged={handleReferenceChange}
              showRegion={
                typeof regionId === 'undefined' ||
                regionId === null ||
                regionId === emptyId
              }
              showBuilding={
                typeof buildingId === 'undefined' ||
                buildingId === null ||
                buildingId === emptyId
              }
              showControlSystem={
                typeof controlSystemId === 'undefined' ||
                controlSystemId === null ||
                controlSystemId === emptyId
              }
              showOrganization={false}
              references={{
                organizationId: userProfile.organization.id,
                regionId: regionId ? regionId : emptyId,
                buildingId: buildingId ? buildingId : emptyId,
                controlSystemId: controlSystemId ? controlSystemId : emptyId,
              }}
            />
          </Col>
        </Row>
        <IBTabs defaultActiveKey="Description" items={getTabItems()} />
      </Form>
    </Modal>
  );
};
export default TicketEntryDialog;
